import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogHeader,
  Icon,
  ToastLevel,
  useIntl,
  useToast,
} from "@metsooutotec/modes-react-components";
import React, { FC, useState } from "react";
import {
  SBSensor,
  SensorBatch,
} from "../sensor-batch/models/sensor-batch-models";
import { NavigationDeleteEntityOptions } from "../../../components/utils/modal-helper";
import sensorBatchStore from "../sensor-batch/stores/sensor-batch-store";
import { StoreResults } from "../../../stores/stores-helper";
import { IotDevicesModalTypes } from "./iot-entity-modal-helper";
import { convertDateToDDMMMYYYY } from "../../../components/utils/date-formatter-utils";
import sensorStore from "features/iot/sensors/stores/sensor-store";

interface IotDeleteEntityProps {
  isOpen: boolean | undefined;
  onClose: (params: NavigationDeleteEntityOptions) => void;
  entity?: SensorBatch | SBSensor;
  modalType: IotDevicesModalTypes;
}

const IotEntityDeleteModal: FC<IotDeleteEntityProps> = ({
  isOpen,
  onClose,
  entity,
  modalType,
}) => {
  const { formatMessage } = useIntl();
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<{
    isError: boolean;
    errorMessage: string;
  }>({ isError: false, errorMessage: "" });
  const { setMessage } = useToast();
  let header, content, entityTitle, warning;

  switch (modalType) {
    case IotDevicesModalTypes.SensorBatchModal:
      header = formatMessage("sensorBatchDelete.header");
      content = formatMessage("sensorBatchDelete.content");
      entityTitle = convertDateToDDMMMYYYY(
        (entity as SensorBatch)?.batchDate as Date
      );
      warning = formatMessage("sensorBatchDelete.warning");
      break;
    case IotDevicesModalTypes.SensorModal:
      header = formatMessage("sensorDelete.header");
      content = formatMessage("sensorDelete.content");
      entityTitle = (entity as SBSensor)?.serialNumber;
      warning = formatMessage("sensorDelete.warning");
      warning = formatMessage("sensorDelete.warning");
      break;
    default:
      header = "";
      content = "";
      entityTitle = "";
      warning = "";
      break;
  }

  const labels = {
    header,
    content,
    entityTitle,
    warning,
  };
  const onConfirm = async (e: React.FormEvent): Promise<void> => {
    try {
      setLoading(true);
      let passed: boolean = false;
      let message: string = "";
      let result: StoreResults = { passed: false, message: "" };
      if (modalType === IotDevicesModalTypes.SensorModal) {
        const sensor = entity as SBSensor;
        const sensorBatch = sensorBatchStore.getSensorBatchBySensorMacAddress(
          sensor.macAddress
        );
        const sensorData = sensorStore.getSensorByMacAddress(sensor.macAddress);
        const nonEmptyCommissionedSensor = [
          sensorData?.provisionings && sensorData?.provisionings?.length > 0,
          sensorData?.provisioningState,
          sensorData?.filteredData && sensorData?.filteredData?.length > 0,
          sensorData?.changeTable && sensorData?.changeTable?.length > 0,
        ];

        if (nonEmptyCommissionedSensor.some(Boolean)) {
          setMessage(
            "This sensor cannot be deleted since it is commissioned or has telemetry data.",
            ToastLevel.ALERT,
            6000
          );
          onClose({ deleted: false });
          return;
        }
        result = await sensorStore.deleteEntity({
          sensorBatchId: sensorBatch?.id as string,
          macAddress: sensor.macAddress,
        });
      } else {
        const sensorbatch = entity as SensorBatch;
        result = await sensorBatchStore.deleteEntitiy(sensorbatch.id);
      }
      passed = result.passed;
      message = result.message;

      if (!passed) {
        setLoading(false);
        setIsError({ isError: true, errorMessage: message });
        return;
      }

      // Handle success

      setLoading(false);
      if (modalType === IotDevicesModalTypes.SensorModal) {
        setMessage(
          `${labels.entityTitle} has been deleted!`,
          ToastLevel.SUCCESS,
          6000
        );
        onClose({ deleted: false });
      }
      if (modalType === IotDevicesModalTypes.SensorBatchModal) {
        onClose({ deleted: true, entityName: labels.entityTitle });
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <Dialog open={isOpen} dimmer size="small" style={{ maxWidth: "696px" }}>
        <DialogHeader>{labels.header}</DialogHeader>
        {isError.isError && (
          <p style={{ color: "red" }}>{isError.errorMessage}</p>
        )}
        <DialogContent data-cy="dialog-content">
          <>
            {labels.content} '{labels?.entityTitle}'.{" "}
            <div
              style={{ width: "100%", textAlign: "center", marginTop: "3em" }}
            >
              <Icon className="icon-info-o-24" /> {labels.warning}
            </div>
          </>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={isLoading}
            onClick={() => onClose({ deleted: false })}
            subtle={true}
          >
            Cancel
          </Button>
          <Button
            disabled={isLoading}
            loading={isLoading}
            onClick={onConfirm}
            primary={true}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
export default IotEntityDeleteModal;
